import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
} from "@chakra-ui/react";
import {
  MdSearch,
  MdCheckCircle,
  MdZoomIn,
  MdLocalHospital,
  MdDirectionsTransit,
  MdPerson,
  MdHelp,
  MdArrowForwardIos,
  MdWarning,
  MdInfoOutline,
  MdLightbulbOutline,
} from "react-icons/md";
import { useNavbarAction } from "contexts/NavbarActionContext";

// Icons from assets
import missionsIcon from "assets/nav-icon/missions.png";
import patientsIcon from "assets/nav-icon/ptients.png";
import vehiculeIcon from "assets/nav-icon/vehicule.png";

// Import des images de documentation
import {
  listMissions,
  etape1,
  etape2,
  etape3,
  ambulanceNoDisplay,
  checkVille,
  ambulanceList,
  ambulanceEdit,
  ambulanceEditModal,
} from "assets/img/documentation/missions/creer_mission/index.js";

// Theme colors
const THEME = {
  primary: "#006133",
  primaryLight: "#00753e",
  primaryDark: "#004d29",
  accent: "#01B574",
  accentLight: "#E6FAF5",
  background: "#F4F7FE",
  cardBg: "#ffffff",
  textPrimary: "#1B2559",
  textSecondary: "#707EAE",
  border: "#E0E5F2",
};

// Documentation Data
const documentationData = {
  missions: {
    title: "Missions",
    icon: missionsIcon,
    iconComponent: MdLocalHospital,
    description: "Créer, gérer et suivre les missions de transport sanitaire",
    color: THEME.primary,
    sections: [
      {
        id: "missions-overview",
        title: "Vue d'ensemble",
        content: `Les missions sont au cœur du système OmniDoc SAMU. Une mission représente une demande de transport sanitaire d'un patient, depuis un établissement de santé vers un autre, avec affectation d'une ambulance et d'une équipe médicale.`,
        subsections: [
          {
            title: "Types de missions",
            items: [
              "Urgente : Transport immédiat avec priorité maximale",
              "Planifiée : Transport programmé à une date/heure spécifique",
              "Aller-Retour : Aller vers destination + retour prévu",
            ],
          },
          {
            title: "États d'une mission",
            items: [
              "Créée : Mission enregistrée, en attente d'affectation",
              "Affectée : Ambulance et équipe assignées",
              "En cours : Transport en cours d'exécution",
              "Terminée : Mission accomplie avec succès",
              "Annulée : Mission annulée avant complétion",
            ],
          },
        ],
      },
      {
        id: "missions-create",
        title: "Créer une mission",
        content: `La création d'une mission se fait en 3 étapes via un assistant (stepper). Chaque étape doit être complétée avant de passer à la suivante.`,
        images: [
          {
            src: listMissions,
            caption: "Dans la liste des missions, cliquez sur le bouton 'Créer une mission'",
            alt: "Bouton créer mission dans la liste"
          }
        ],
        steps: [
          {
            title: "Informations de mission",
            image: {
              src: etape1,
              caption: "Formulaire étape 1 : Les champs avec * sont obligatoires",
              alt: "Formulaire de création mission étape 1"
            },
            details: [
              "Les champs marqués d'un astérisque (*) sont OBLIGATOIRES",
              "Sélectionner l'état de mission (Urgente ou Planifiée)",
              "Si Urgente : choisir la gravité (Urgence vitale, Urgence élevée, Non vitale)",
              "Renseigner les informations patient (IPP du patient, téléphone)",
              "Sélectionner la ville de mission (obligatoire pour filtrer les ambulances)",
            ],
            warning: "Si vous laissez un champ obligatoire vide, la création sera bloquée."
          },
          {
            title: "Affectation d'ambulance",
            image: {
              src: etape2,
              caption: "Liste des ambulances disponibles dans la ville sélectionnée",
              alt: "Sélection d'ambulance avec carte"
            },
            details: [
              "Visualiser les ambulances disponibles filtrées par la ville de mission",
              "Chaque ambulance affiche : nom/code, type (TAR), distance et temps estimé",
              "La carte interactive montre la position géographique des ambulances",
              "Sélectionner une ambulance en cliquant dessus",
            ],
            note: "Seules les ambulances disponibles dans la ville sélectionnée sont affichées."
          },
          {
            title: "Finalisation et infos patient",
            image: {
              src: etape3,
              caption: "Mission créée - Compléter les informations du patient",
              alt: "Formulaire patient final"
            },
            details: [
              "Mission créée avec succès !",
              "Compléter obligatoirement : Nom, Prénom, Âge, Sexe",
              "CIN et IPP sont facultatifs",
              "Cliquer sur 'Mettre à jour la mission' pour finaliser",
            ],
            success: "Complétez les informations patient pour que la mission soit fonctionnelle."
          },
        ],
        tips: [
          "Le système affiche un message 'Mission créée avec succès' dès la fin de l'étape 2",
          "Les ambulances affichées dépendent de la ville et du type sélectionné",
          "La distance et le temps d'arrivée sont calculés automatiquement",
        ],
      },
      {
        id: "missions-troubleshooting",
        title: "Dépannage",
        content: `Solutions aux problèmes les plus fréquents lors de la création d'une mission.`,
        troubleshooting: [
          {
            problem: "Aucune ambulance ne s'affiche à l'étape 2",
            solution: "Plusieurs causes possibles :",
            image: {
              src: ambulanceNoDisplay,
              caption: "Cliquez sur 'Refetch' ou vérifiez votre sélection",
              alt: "Écran sans ambulances"
            },
            steps: [
              "Cliquez sur le bouton 'Refetch' pour actualiser la liste",
              "Vérifiez que la ville sélectionnée contient des ambulances disponibles",
              "Assurez-vous que le type d'urgence correspond aux ambulances disponibles",
              "Contactez l'administrateur si le problème persiste"
            ]
          },
          {
            problem: "Vérification de la ville sélectionnée",
            solution: "Assurez-vous que la ville est correctement sélectionnée :",
            image: {
              src: checkVille,
              caption: "La ville filtre les ambulances disponibles",
              alt: "Sélection de ville"
            },
            steps: [
              "Vérifiez que la ville de mission est bien sélectionnée",
              "Confirmez que cette ville contient effectivement des ambulances",
              "Si besoin, changez de ville et revenez à l'étape 2",
            ]
          },
          {
            problem: "Modifier la disponibilité ou l'affectation d'une ambulance",
            solution: "Si aucune ambulance n'est disponible, rendez-vous dans Gestion des ressources → Ambulances pour corriger le statut ou assigner un ambulancier :",
            image: {
              src: ambulanceList,
              caption: "Étape 1 — Recherchez l'ambulance à modifier dans la liste",
              alt: "Liste des ambulances avec barre de recherche"
            },
            steps: [
              "Accédez à Gestion des ressources → Ambulances depuis le menu latéral",
              "Tapez l'immatriculation dans la barre de recherche pour retrouver l'ambulance",
              "Cliquez sur l'icône ⋮ (Actions) de la ligne puis sélectionnez 'Modifier' (voir image ci-dessous)",
              "Dans le modal 'Modifier le Véhicule' — section gauche : changez le Statut à 'Disponible' si nécessaire",
              "Section droite : sélectionnez un Ambulancier dans la liste déroulante si aucun n'est affecté",
              "Cliquez sur 'Sauvegarder', puis revenez à la création de la mission et rechargez les ambulances",
            ],
            extraImages: [
              {
                src: ambulanceEdit,
                caption: "Étape 2 — Cliquez sur ⋮ puis 'Modifier'",
                alt: "Menu actions ambulance"
              },
              {
                src: ambulanceEditModal,
                caption: "Étape 3 — Modifiez le Statut (disponibilité) ou assignez un Ambulancier",
                alt: "Modal modifier le véhicule"
              }
            ]
          }
        ],
      },
      {
        id: "missions-list",
        title: "Liste des missions",
        content: `La page de liste affiche toutes les missions avec des filtres avancés et des actions rapides.`,
        features: [
          "Recherche par IPP, nom patient, numéro de mission",
          "Filtres par statut, date, ville, type d'urgence",
          "Tri par date, statut, urgence",
          "Actions : voir détails, modifier, dupliquer, supprimer",
          "Export des données (selon permissions)",
        ],
      },
    ],
  },
  patients: {
    title: "Patients",
    icon: patientsIcon,
    iconComponent: MdPerson,
    description: "Enregistrer et gérer les dossiers patients",
    color: "#3965FF",
    sections: [
      {
        id: "patients-overview",
        title: "Vue d'ensemble",
        content: `Le module Patients permet de gérer les informations des patients transportés. Chaque patient est identifié par un IPP (Identifiant Permanent du Patient) unique.`,
      },
      {
        id: "patients-create",
        title: "Créer un patient",
        content: `L'ajout d'un patient peut se faire depuis le module Patients ou directement lors de la création d'une mission.`,
        fields: [
          { name: "IPP", required: true, description: "Identifiant unique du patient" },
          { name: "Nom", required: true, description: "Nom de famille" },
          { name: "Prénom", required: true, description: "Prénom" },
          { name: "Date de naissance", required: false, description: "Format JJ/MM/AAAA" },
          { name: "Sexe", required: true, description: "Homme ou Femme" },
          { name: "Téléphone", required: true, description: "Format +212XXXXXXXXX" },
          { name: "CIN", required: false, description: "Carte d'identité nationale" },
          { name: "Adresse", required: false, description: "Adresse complète" },
        ],
      },
      {
        id: "patients-search",
        title: "Rechercher un patient",
        content: `La recherche de patients supporte plusieurs critères pour retrouver rapidement un dossier.`,
        criteria: ["IPP (recherche exacte)", "Nom / Prénom (recherche partielle)", "Téléphone", "CIN"],
      },
    ],
  },
  ambulances: {
    title: "Ambulances",
    icon: vehiculeIcon,
    iconComponent: MdDirectionsTransit,
    description: "Gérer la flotte de véhicules et leur disponibilité",
    color: "#FFB547",
    sections: [
      {
        id: "ambulances-overview",
        title: "Vue d'ensemble",
        content: `Le module Ambulances permet de gérer l'ensemble de la flotte de véhicules sanitaires, leur statut et leur affectation au personnel.`,
        subsections: [
          {
            title: "Types de véhicules",
            items: [
              "TAR (Transport Ambulancier Régulé) : Pour patients nécessitant surveillance médicale"
            ],
          },
          {
            title: "Statuts des véhicules",
            items: [
              "Disponible : Prêt pour affectation",
              "En mission : Actuellement en intervention",
              "Maintenance : En réparation ou révision",
              "Hors service : Indisponible",
            ],
          },
        ],
      },
      {
        id: "ambulances-create",
        title: "Ajouter un véhicule",
        content: `L'ajout d'un véhicule nécessite les informations d'immatriculation et les caractéristiques techniques.`,
        fields: [
          { name: "Immatriculation", required: true, description: "Plaque d'immatriculation unique" },
          { name: "Type", required: true, description: "TAR, TAS ou VSL" },
          { name: "Ville", required: true, description: "Ville d'affectation" },
          { name: "Ambulancier", required: false, description: "Conducteur attitré" },
          { name: "Statut", required: true, description: "État actuel du véhicule" },
        ],
      },
      {
        id: "ambulances-tracking",
        title: "Suivi GPS",
        content: `Le suivi GPS permet de localiser en temps réel tous les véhicules de la flotte sur une carte interactive.`,
        features: [
          "Position en temps réel sur Google Maps",
          "Historique des trajets",
          "Calcul automatique de la distance au point de prise en charge",
          "Estimation du temps d'arrivée (ETA)",
        ],
      },
    ],
  },
};

// FAQ Data
const faqData = [
  {
    question: "Que se passe-t-il si je laisse un champ obligatoire (*) vide ?",
    answer: "Les champs marqués d'un astérisque (*) sont obligatoires. Si vous laissez un champ requis vide, le système vous empêchera de passer à l'étape suivante.",
    category: "Missions",
  },
  {
    question: "Pourquoi ne vois-je que certaines ambulances ?",
    answer: "Les ambulances affichées sont filtrées selon la ville de mission sélectionnée et le type d'ambulance requis. Pour les missions urgentes, seules les ambulances disponibles sont proposées.",
    category: "Missions",
  },
  {
    question: "Que signifie 'Mission créée avec succès' ?",
    answer: "Ce message apparaît après l'étape 2, mais vous devez obligatoirement compléter l'étape 3 avec les informations du patient (nom, prénom, âge, sexe).",
    category: "Missions",
  },
  {
    question: "Quels sont les champs obligatoires pour le patient ?",
    answer: "Pour finaliser une mission : Nom, Prénom, Âge et Sexe du patient. L'IPP et le CIN sont optionnels.",
    category: "Patients",
  },
  {
    question: "Comment la distance est-elle calculée ?",
    answer: "Le système calcule automatiquement la distance (en km) et le temps estimé entre la position de chaque ambulance et le lieu de mission.",
    category: "Ambulances",
  },
  {
    question: "Que faire si aucune ambulance ne s'affiche ?",
    answer: "Cliquez sur 'Refetch' pour actualiser, vérifiez que la ville contient des ambulances disponibles, ou contactez l'administrateur.",
    category: "Ambulances",
  },
  {
    question: "Comment modifier le statut d'une ambulance ?",
    answer: "Accédez au module Ambulances, cliquez sur le véhicule concerné, puis sur 'Modifier'. Changez le statut et sauvegardez.",
    category: "Ambulances",
  },
];

// Image Preview Component
const ImagePreview = ({ isOpen, onClose, src, alt, caption }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
    <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(10px)" />
    <ModalContent bg="transparent" boxShadow="none" maxW="95vw" maxH="95vh">
      <ModalCloseButton
        color="white"
        bg="blackAlpha.600"
        borderRadius="full"
        top={4}
        right={4}
        _hover={{ bg: "blackAlpha.800" }}
      />
      <ModalBody p={4}>
        <VStack spacing={4}>
          <Image
            src={src}
            alt={alt}
            maxW="100%"
            maxH="85vh"
            objectFit="contain"
            borderRadius="16px"
            boxShadow="2xl"
          />
          {caption && (
            <Text
              fontSize="14px"
              color="white"
              textAlign="center"
              bg="blackAlpha.600"
              px={4}
              py={2}
              borderRadius="full"
            >
              {caption}
            </Text>
          )}
        </VStack>
      </ModalBody>
    </ModalContent>
  </Modal>
);

// Section Content Component
const SectionContent = ({ section, moduleColor }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState({ src: '', alt: '', caption: '' });

  const handleImageClick = (src, alt, caption) => {
    setSelectedImage({ src, alt, caption });
    onOpen();
  };

  return (
    <VStack align="stretch" spacing={5}>
      {section.content && (
        <Text fontSize="16px" color={THEME.textSecondary} lineHeight="1.8">
          {section.content}
        </Text>
      )}

      {section.subsections && section.subsections.map((sub, idx) => (
        <Box key={idx} bg={THEME.background} p={4} borderRadius="12px">
          <Text fontWeight="600" fontSize="16px" mb={3} color={THEME.textPrimary}>
            {sub.title}
          </Text>
          <VStack align="stretch" spacing={2}>
            {sub.items.map((item, i) => (
              <HStack key={i} spacing={3} align="start">
                <Icon as={MdCheckCircle} color={moduleColor || THEME.accent} mt={0.5} />
                <Text fontSize="15px" color={THEME.textSecondary}>{item}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      ))}

      {section.images && section.images.map((img, idx) => (
        <Box
          key={idx}
          p={4}
          bg={THEME.background}
          borderRadius="16px"
          border="1px solid"
          borderColor={THEME.border}
        >
          <HStack mb={3} spacing={2}>
            <Icon as={MdInfoOutline} color={moduleColor || THEME.primary} />
            <Text fontWeight="600" fontSize="15px" color={THEME.textPrimary}>
              Capture d'écran
            </Text>
          </HStack>
          <Tooltip label="Cliquez pour agrandir" placement="top">
            <Image
              src={img.src}
              alt={img.alt}
              borderRadius="12px"
              w="100%"
              maxW="100%"
              cursor="pointer"
              transition="all 0.3s"
              _hover={{ transform: "scale(1.01)", boxShadow: "xl" }}
              onClick={() => handleImageClick(img.src, img.alt, img.caption)}
            />
          </Tooltip>
          <HStack mt={3} spacing={2}>
            <Icon as={MdZoomIn} color={THEME.textSecondary} boxSize={4} />
            <Text fontSize="14px" color={THEME.textSecondary}>{img.caption}</Text>
          </HStack>
        </Box>
      ))}

      <ImagePreview
        isOpen={isOpen}
        onClose={onClose}
        src={selectedImage.src}
        alt={selectedImage.alt}
        caption={selectedImage.caption}
      />

      {section.steps && (
        <VStack align="stretch" spacing={4}>
          {section.steps.map((step, idx) => (
            <Box
              key={idx}
              p={5}
              bg="white"
              borderRadius="16px"
              border="1px solid"
              borderColor={THEME.border}
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                w="4px"
                h="100%"
                bg={moduleColor || THEME.primary}
              />
              <HStack spacing={4} mb={4}>
                <Flex
                  w="36px"
                  h="36px"
                  bg={moduleColor || THEME.primary}
                  borderRadius="10px"
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Text fontSize="16px" fontWeight="700" color="white">{idx + 1}</Text>
                </Flex>
                <Text fontWeight="600" fontSize="17px" color={THEME.textPrimary}>
                  {step.title}
                </Text>
              </HStack>

              {step.image && (
                <Box mb={4} bg={THEME.background} p={3} borderRadius="12px">
                  <Tooltip label="Cliquez pour agrandir" placement="top">
                    <Image
                      src={step.image.src}
                      alt={step.image.alt}
                      borderRadius="10px"
                      w="100%"
                      maxW="100%"
                      cursor="pointer"
                      transition="all 0.3s"
                      _hover={{ transform: "scale(1.01)", boxShadow: "lg" }}
                      onClick={() => handleImageClick(step.image.src, step.image.alt, step.image.caption)}
                    />
                  </Tooltip>
                  <HStack mt={2} spacing={2}>
                    <Icon as={MdZoomIn} color={THEME.textSecondary} boxSize={4} />
                    <Text fontSize="13px" color={THEME.textSecondary}>{step.image.caption}</Text>
                  </HStack>
                </Box>
              )}

              <VStack align="stretch" spacing={2} pl={2}>
                {step.details.map((detail, i) => (
                  <HStack key={i} spacing={3} align="start">
                    <Icon as={MdArrowForwardIos} color={THEME.textSecondary} boxSize={3} mt={1} />
                    <Text fontSize="15px" color={THEME.textSecondary}>{detail}</Text>
                  </HStack>
                ))}
              </VStack>

              {step.warning && (
                <HStack mt={4} p={3} bg="#FEF3F2" borderRadius="10px" spacing={3} border="1px solid #FECDCA">
                  <Icon as={MdWarning} color="#D92D20" boxSize={5} />
                  <Text fontSize="14px" color="#B42318">{step.warning}</Text>
                </HStack>
              )}
              {step.note && (
                <HStack mt={4} p={3} bg="#EFF8FF" borderRadius="10px" spacing={3} border="1px solid #B2DDFF">
                  <Icon as={MdLightbulbOutline} color="#1570EF" boxSize={5} />
                  <Text fontSize="14px" color="#175CD3">{step.note}</Text>
                </HStack>
              )}
              {step.success && (
                <HStack mt={4} p={3} bg={THEME.accentLight} borderRadius="10px" spacing={3} border="1px solid #6CE9A6">
                  <Icon as={MdCheckCircle} color={THEME.accent} boxSize={5} />
                  <Text fontSize="14px" color="#027A48">{step.success}</Text>
                </HStack>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {section.troubleshooting && (
        <VStack align="stretch" spacing={4}>
          {section.troubleshooting.map((item, idx) => (
            <Box key={idx} p={5} bg="white" borderRadius="16px" border="1px solid" borderColor="#FECDCA">
              <HStack spacing={3} mb={3}>
                <Flex w="32px" h="32px" bg="#FEF3F2" borderRadius="8px" align="center" justify="center">
                  <Icon as={MdWarning} color="#D92D20" />
                </Flex>
                <Text fontWeight="600" fontSize="16px" color="#B42318">{item.problem}</Text>
              </HStack>
              {item.solution && (
                <Text fontSize="14px" color={THEME.textSecondary} mb={3}>{item.solution}</Text>
              )}
              {item.image && (
                <Box mb={4} bg={THEME.background} p={3} borderRadius="12px">
                  <Tooltip label="Cliquez pour agrandir" placement="top">
                    <Image
                      src={item.image.src}
                      alt={item.image.alt}
                      borderRadius="10px"
                      w="100%"
                      maxW="100%"
                      cursor="pointer"
                      transition="all 0.3s"
                      _hover={{ transform: "scale(1.01)", boxShadow: "lg" }}
                      onClick={() => handleImageClick(item.image.src, item.image.alt, item.image.caption)}
                    />
                  </Tooltip>
                  <Text fontSize="13px" color={THEME.textSecondary} mt={2}>{item.image.caption}</Text>
                </Box>
              )}
              <Box bg={THEME.accentLight} p={4} borderRadius="10px">
                <Text fontWeight="600" fontSize="14px" color="#027A48" mb={3}>Solutions :</Text>
                <VStack align="stretch" spacing={2}>
                  {item.steps.map((step, i) => (
                    <HStack key={i} spacing={3} align="start">
                      <Flex w="20px" h="20px" bg={THEME.accent} borderRadius="full" align="center" justify="center" flexShrink={0}>
                        <Text fontSize="10px" fontWeight="600" color="white">{i + 1}</Text>
                      </Flex>
                      <Text fontSize="14px" color="#027A48">{step}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              {item.extraImages && item.extraImages.length > 0 && (
                <VStack mt={4} spacing={3} align="stretch">
                  {item.extraImages.map((img, i) => (
                    <Box key={i} bg={THEME.background} p={3} borderRadius="12px">
                      <Tooltip label="Cliquez pour agrandir" placement="top">
                        <Image
                          src={img.src}
                          alt={img.alt}
                          borderRadius="10px"
                          w="100%"
                          maxW="100%"
                          cursor="pointer"
                          transition="all 0.3s"
                          _hover={{ transform: "scale(1.01)", boxShadow: "lg" }}
                          onClick={() => handleImageClick(img.src, img.alt, img.caption)}
                        />
                      </Tooltip>
                      <Text fontSize="13px" color={THEME.textSecondary} mt={2}>{img.caption}</Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          ))}
        </VStack>
      )}

      {section.fields && (
        <TableContainer border="1px solid" borderColor={THEME.border} borderRadius="12px" overflow="hidden">
          <Table variant="simple" size="sm">
            <Thead bg={THEME.background}>
              <Tr>
                <Th color={THEME.textPrimary} fontSize="14px" py={3}>Champ</Th>
                <Th color={THEME.textPrimary} fontSize="14px" py={3}>Requis</Th>
                <Th color={THEME.textPrimary} fontSize="14px" py={3}>Description</Th>
              </Tr>
            </Thead>
            <Tbody>
              {section.fields.map((field, idx) => (
                <Tr key={idx} _hover={{ bg: THEME.background }}>
                  <Td fontWeight="500" fontSize="15px" color={THEME.textPrimary}>{field.name}</Td>
                  <Td>
                    {field.required ? (
                      <Badge bg="#FEF3F2" color="#B42318" fontSize="12px" px={2} py={1} borderRadius="full">Obligatoire</Badge>
                    ) : (
                      <Badge bg={THEME.background} color={THEME.textSecondary} fontSize="12px" px={2} py={1} borderRadius="full">Optionnel</Badge>
                    )}
                  </Td>
                  <Td fontSize="15px" color={THEME.textSecondary}>{field.description}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {section.features && (
        <Box bg={THEME.background} p={4} borderRadius="12px">
          <VStack align="stretch" spacing={2}>
            {section.features.map((feature, idx) => (
              <HStack key={idx} spacing={3} align="start">
                <Icon as={MdCheckCircle} color={moduleColor || THEME.accent} mt={0.5} />
                <Text fontSize="15px" color={THEME.textSecondary}>{feature}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {section.tips && (
        <Box bg="#FFFAEB" p={4} borderRadius="12px" border="1px solid #FEF0C7">
          <HStack spacing={2} mb={3}>
            <Icon as={MdLightbulbOutline} color="#DC6803" />
            <Text fontWeight="600" fontSize="15px" color="#B54708">Conseils</Text>
          </HStack>
          <VStack align="stretch" spacing={2}>
            {section.tips.map((tip, idx) => (
              <HStack key={idx} spacing={3} align="start">
                <Box w="6px" h="6px" bg="#DC6803" borderRadius="full" mt={1.5} flexShrink={0} />
                <Text fontSize="14px" color="#B54708">{tip}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      {section.criteria && (
        <Box bg={THEME.background} p={4} borderRadius="12px">
          <VStack align="stretch" spacing={2}>
            {section.criteria.map((c, idx) => (
              <HStack key={idx} spacing={3} align="start">
                <Icon as={MdSearch} color={moduleColor || THEME.primary} mt={0.5} />
                <Text fontSize="15px" color={THEME.textSecondary}>{c}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

// Sidebar Navigation Item
const SidebarNavItem = ({ title, description, icon, iconComponent, color, isActive, onClick }) => (
  <Box
    as="button"
    w="100%"
    py="10px"
    px={3}
    borderRadius="10px"
    bg={isActive ? color : "transparent"}
    color={isActive ? "white" : THEME.textSecondary}
    transition="all 0.2s"
    _hover={{ bg: isActive ? color : THEME.background, color: isActive ? "white" : THEME.textPrimary }}
    onClick={onClick}
    textAlign="left"
  >
    <HStack spacing={3}>
      <Flex
        w="32px"
        h="32px"
        bg={isActive ? "whiteAlpha.200" : THEME.background}
        borderRadius="8px"
        align="center"
        justify="center"
        flexShrink={0}
      >
        {icon ? (
          <Image src={icon} alt={title} boxSize="18px" />
        ) : (
          <Icon as={iconComponent} boxSize={4} color={isActive ? "white" : color} />
        )}
      </Flex>
      <Box minW={0}>
        <Text fontSize="13px" fontWeight={isActive ? "600" : "500"} lineHeight="1.3">
          {title}
        </Text>
        <Text fontSize="11px" color={isActive ? "whiteAlpha.700" : THEME.textSecondary} noOfLines={1}>
          {description}
        </Text>
      </Box>
    </HStack>
  </Box>
);

// Main Documentation Component
export default function Documentation() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeModule, setActiveModule] = useState("missions");
  const { navbarHeight } = useNavbarAction();
  const sectionRefs = useRef({});
  const contentRef = useRef(null);

  const topOffset = navbarHeight > 0 ? navbarHeight : 80;

  // Filter modules based on search
  const filteredModules = useMemo(() => {
    if (!searchQuery.trim()) return Object.entries(documentationData);
    const query = searchQuery.toLowerCase();
    return Object.entries(documentationData).filter(([, module]) =>
      module.title.toLowerCase().includes(query) ||
      module.description.toLowerCase().includes(query) ||
      module.sections.some(
        (s) =>
          s.title.toLowerCase().includes(query) ||
          s.content?.toLowerCase().includes(query)
      )
    );
  }, [searchQuery]);

  const filteredFaq = useMemo(() => {
    if (!searchQuery.trim()) return faqData;
    const query = searchQuery.toLowerCase();
    return faqData.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const showFaq = !searchQuery.trim() || filteredFaq.length > 0;

  // Scroll to section inside the content container
  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    const container = contentRef.current;
    if (el && container) {
      const elTop = el.getBoundingClientRect().top;
      const containerTop = container.getBoundingClientRect().top;
      container.scrollTo({ top: container.scrollTop + elTop - containerTop - 16, behavior: "smooth" });
    }
    setActiveModule(key);
  };

  // Highlight active sidebar item while scrolling inside the content container
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const allKeys = [
      ...Object.keys(documentationData),
      ...(showFaq ? ["faq"] : []),
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveModule(visible[0].target.dataset.moduleKey);
        }
      },
      {
        root: container,
        rootMargin: "0px 0px -60% 0px",
        threshold: 0,
      }
    );

    allKeys.forEach((key) => {
      const el = sectionRefs.current[key];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [filteredModules.length, showFaq]);

  const navItems = [
    ...filteredModules.map(([key, mod]) => ({
      key,
      title: mod.title,
      description: mod.description,
      icon: mod.icon,
      iconComponent: mod.iconComponent,
      color: mod.color,
    })),
    ...(showFaq
      ? [{
          key: "faq",
          title: "FAQ",
          description: "Questions fréquentes",
          icon: null,
          iconComponent: MdHelp,
          color: THEME.primary,
        }]
      : []),
  ];

  return (
    // Outer: full viewport height, no page scroll
    <Box
      h="100vh"
      overflow="hidden"
      display="flex"
      flexDir="column"
      bg={THEME.background}
    >
      {/* Navbar spacer */}
      <Box h={`${topOffset}px`} flexShrink={0} transition="height 0.3s ease" />

      {/* Fixed Search Bar */}
      <Box flexShrink={0} px={{ base: 4, lg: 6 }} py={3} bg={THEME.background}>
        <Box maxW="1600px" mx="auto">
          <Card
            bg={THEME.cardBg}
            borderRadius="20px"
            p={4}
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          >
            <InputGroup maxW="500px">
              <InputLeftElement h="48px">
                <Icon as={MdSearch} color={THEME.textSecondary} boxSize={5} />
              </InputLeftElement>
              <Input
                placeholder="Rechercher dans la documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg={THEME.background}
                border="none"
                borderRadius="12px"
                fontSize="14px"
                h="48px"
                _focus={{ boxShadow: `0 0 0 2px ${THEME.primary}` }}
                _placeholder={{ color: THEME.textSecondary }}
              />
            </InputGroup>
          </Card>
        </Box>
      </Box>

      {/* Body row: sidebar (fixed) + content (scrolls) */}
      <Flex flex="1" overflow="hidden" px={{ base: 4, lg: 6 }} pb={4} gap={6} maxW="1600px" mx="auto" w="100%">

          {/* Sidebar — never scrolls, always visible */}
          <Box
            w="300px"
            display={{ base: "none", lg: "flex" }}
            flexDir="column"
            flexShrink={0}
            overflowY="auto"
            sx={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { bg: "transparent" },
              "&::-webkit-scrollbar-thumb": { bg: THEME.border, borderRadius: "full" },
            }}
          >
            <Card
              bg={THEME.cardBg}
              borderRadius="20px"
              p={4}
              boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            >
              <Text
                fontSize="11px"
                fontWeight="600"
                color={THEME.textSecondary}
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb={4}
                px={2}
              >
                Modules
              </Text>

              <VStack align="stretch" spacing={1}>
                {navItems.map(({ key, ...item }) => (
                  <React.Fragment key={key}>
                    {key === "faq" && <Box h="1px" bg={THEME.border} my={2} />}
                    <SidebarNavItem
                      {...item}
                      isActive={activeModule === key}
                      onClick={() => scrollToSection(key)}
                    />
                  </React.Fragment>
                ))}
              </VStack>
            </Card>
          </Box>

          {/* Scrollable content area */}
          <Box ref={contentRef} flex="1" overflowY="auto" minW={0} py={2} pr={1}
            sx={{
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-track": { bg: "transparent" },
              "&::-webkit-scrollbar-thumb": { bg: THEME.border, borderRadius: "full" },
            }}
          >
            <VStack align="stretch" spacing={8} pb={6}>

              {filteredModules.map(([key, module]) => (
                <Box
                  key={key}
                  ref={(el) => { sectionRefs.current[key] = el; }}
                  data-module-key={key}
                >
                  {/* Module Header */}
                  <Card
                    bg={THEME.cardBg}
                    borderRadius="20px"
                    p={6}
                    mb={4}
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                    position="relative"
                    overflow="hidden"
                  >
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      w="100%"
                      h="4px"
                      bg={module.color}
                    />
                    <HStack spacing={5}>
                      <Flex
                        w="60px"
                        h="60px"
                        bg={`${module.color}15`}
                        borderRadius="16px"
                        align="center"
                        justify="center"
                      >
                        <Image src={module.icon} alt={module.title} boxSize="40px" />
                      </Flex>
                      <Box>
                        <Heading size="md" color={THEME.textPrimary} fontWeight="700">
                          {module.title}
                        </Heading>
                        <Text color={THEME.textSecondary} fontSize="16px" mt={1}>
                          {module.description}
                        </Text>
                      </Box>
                    </HStack>
                  </Card>

                  {/* Module Sections */}
                  <VStack align="stretch" spacing={4}>
                    {module.sections.map((section) => (
                      <Card
                        key={section.id}
                        bg={THEME.cardBg}
                        borderRadius="20px"
                        p={6}
                        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                      >
                        <Heading
                          size="sm"
                          color={THEME.textPrimary}
                          fontWeight="600"
                          mb={4}
                          pb={3}
                          borderBottom="1px solid"
                          borderColor={THEME.border}
                        >
                          {section.title}
                        </Heading>
                        <SectionContent section={section} moduleColor={module.color} />
                      </Card>
                    ))}
                  </VStack>
                </Box>
              ))}

              {/* FAQ Section */}
              {showFaq && (
                <Box
                  ref={(el) => { sectionRefs.current["faq"] = el; }}
                  data-module-key="faq"
                >
                  <Card
                    bg={THEME.cardBg}
                    borderRadius="20px"
                    p={6}
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                  >
                    <HStack spacing={4} mb={6}>
                      <Flex
                        w="48px"
                        h="48px"
                        bg={`${THEME.primary}15`}
                        borderRadius="14px"
                        align="center"
                        justify="center"
                      >
                        <Icon as={MdHelp} boxSize={6} color={THEME.primary} />
                      </Flex>
                      <Box>
                        <Heading size="md" color={THEME.textPrimary} fontWeight="700">
                          Questions fréquentes
                        </Heading>
                        <Text color={THEME.textSecondary} fontSize="16px" mt={1}>
                          Trouvez rapidement des réponses à vos questions
                        </Text>
                      </Box>
                    </HStack>

                    <Accordion allowMultiple>
                      {filteredFaq.map((faq, idx) => (
                        <AccordionItem key={idx} border="none" mb={3}>
                          <AccordionButton
                            bg={THEME.background}
                            borderRadius="12px"
                            py={4}
                            px={5}
                            _hover={{ bg: THEME.border }}
                            _expanded={{
                              bg: THEME.primary,
                              color: "white",
                              borderBottomRadius: 0,
                            }}
                          >
                            <Box flex="1" textAlign="left">
                              <HStack spacing={3}>
                                <Badge
                                  bg="whiteAlpha.200"
                                  color="inherit"
                                  fontSize="10px"
                                  px={2}
                                  py={1}
                                  borderRadius="full"
                                >
                                  {faq.category}
                                </Badge>
                                <Text fontWeight="500" fontSize="16px">
                                  {faq.question}
                                </Text>
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel
                            bg={THEME.background}
                            borderBottomRadius="12px"
                            fontSize="16px"
                            color={THEME.textSecondary}
                            lineHeight="1.8"
                            py={4}
                            px={5}
                          >
                            {faq.answer}
                          </AccordionPanel>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Card>
                </Box>
              )}

              {/* Support Card */}
              <Card
                bg={THEME.primary}
                borderRadius="20px"
                p={6}
                boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
              >
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                  <Box>
                    <Text fontWeight="600" fontSize="16px" color="white">
                      Besoin d'aide ?
                    </Text>
                    <Text fontSize="13px" color="whiteAlpha.800" mt={1}>
                      Notre équipe support est disponible pour vous aider
                    </Text>
                  </Box>
                  <Box
                    as="a"
                    href="mailto:support@omnidoc.ma"
                    bg="white"
                    color={THEME.primary}
                    px={5}
                    py={3}
                    borderRadius="12px"
                    fontSize="14px"
                    fontWeight="600"
                    transition="all 0.2s"
                    _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                  >
                    support@omnidoc.ma
                  </Box>
                </Flex>
              </Card>

            </VStack>
          </Box>
        </Flex>
      </Box>
  );
}
